import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { SurveyType } from '../../../../../shared/constants';
import {
  INSIGHTS_SURVEY_TYPES,
  SURVEY_TYPE_DISPLAY_NAME,
} from '../../../../../shared/surveys';
import { fetchCustomerSurveyHistory } from '../../../../../store/customers/actionCreators';
import { fetchOwnerSurveyInsights } from '../../../../../store/hubOwner/actionCreators';
import { getBrowserTimeZone } from '../../../../../shared/utils';
import LoadingSpinner from '../../../../Shared/LoadingSpinner';
import { dimGrey, lightBlack } from '../../../../../mainTheme';
import SurveyInsightsView from './SurveyInsightsView';
import {
  DEFAULT_INSIGHTS_WINDOW,
  findSurveyBlock,
  formatPeriodRange,
  groupRowsBySurveyType,
  INSIGHTS_WINDOWS,
  InsightsWindow,
  NO_SURVEY_ROWS,
} from './surveyInsights';

interface InsightsTabProps {
  ownerId: number;
  // Survey type to open on (from the route state another page navigated
  // with); unknown values fall back to the first type offered.
  initialSurveyType?: SurveyType;
}

const toggleSx = { px: 2, textTransform: 'none', fontWeight: 700 };

// Patient Insights: one dashboard view per survey type over a rolling window.
// Window metrics come from GET /owners/:id/survey_insights (all types in one
// call) and the response list from GET /customers/:id/satisfaction_survey,
// both asked for the same `days` and the viewer's `time_zone`, so the server
// resolves one window (ending today in the browser's zone) for every panel
// and its day buckets match the local times the table shows.

// Resolved once: the browser's zone does not change while the app runs.
const BROWSER_TIME_ZONE = getBrowserTimeZone();
const InsightsTab: React.FC<InsightsTabProps> = ({
  ownerId,
  initialSurveyType,
}) => {
  const dispatch = useDispatch();
  const [surveyType, setSurveyType] = useState<SurveyType>(() =>
    initialSurveyType !== undefined &&
    INSIGHTS_SURVEY_TYPES.includes(initialSurveyType)
      ? initialSurveyType
      : INSIGHTS_SURVEY_TYPES[0],
  );
  const [insightsWindow, setInsightsWindow] = useState<InsightsWindow>(
    DEFAULT_INSIGHTS_WINDOW,
  );

  // Field-level selectors so the tab only re-renders for its own data.
  const ownerSurveyInsights = useSelector(
    (state: RootState) => state.hubOwner.ownerSurveyInsights,
  );
  const ownerSurveyInsightsError = useSelector(
    (state: RootState) => state.hubOwner.ownerSurveyInsightsError,
  );
  const customerSurveyHistory = useSelector(
    (state: RootState) => state.customers.customerSurveyHistory,
  );
  const customerSurveyHistoryError = useSelector(
    (state: RootState) => state.customers.customerSurveyHistoryError,
  );

  // The API counts today as one of its `days`, so the label's N goes as-is.
  const apiDays = insightsWindow.days;
  const insightsForSelection =
    ownerSurveyInsights?.ownerId === ownerId &&
    ownerSurveyInsights.requestedDays === apiDays
      ? ownerSurveyInsights
      : null;
  // Rows for this patient and window. `historyForOwner` is looser (any
  // window): it only decides whether something of this patient's is already
  // on screen, so a window change keeps the view mounted with the list
  // pending instead of swapping in the full-page spinner.
  const historyForOwner =
    customerSurveyHistory?.customerId === ownerId
      ? customerSurveyHistory
      : null;
  const historyForSelection =
    historyForOwner?.requestedDays === apiDays ? historyForOwner : null;

  // Fetch whenever the cache does not match the current patient/window. The
  // slice's loading flag is deliberately not a gate: a request for another
  // patient may still be in flight when this tab mounts, and waiting on it
  // would leave this patient unfetched. Overlapping requests are safe because
  // the thunks carry a sequence id and the reducers drop superseded responses.
  // A failed fetch leaves the cache unchanged, so it is not retried in a loop.
  useEffect(() => {
    if (!insightsForSelection) {
      dispatch(
        fetchOwnerSurveyInsights(
          ownerId,
          apiDays,
          insightsWindow.granularity,
          BROWSER_TIME_ZONE,
        ),
      );
    }
  }, [
    dispatch,
    ownerId,
    apiDays,
    insightsWindow.granularity,
    insightsForSelection,
  ]);

  useEffect(() => {
    if (!historyForSelection) {
      dispatch(fetchCustomerSurveyHistory(ownerId, apiDays, BROWSER_TIME_ZONE));
    }
  }, [dispatch, ownerId, apiDays, historyForSelection]);

  const groups = useMemo(
    () => groupRowsBySurveyType(historyForSelection?.rows),
    [historyForSelection],
  );
  const rows = groups[surveyType] ?? NO_SURVEY_ROWS;
  const period = insightsForSelection?.period;
  const periodRange = formatPeriodRange(period);
  // Errors only count when nothing is cached for this selection: a stale
  // error from an earlier selection must not mask data that did load.
  const insightsError = insightsForSelection ? null : ownerSurveyInsightsError;
  const historyError = historyForSelection ? null : customerSurveyHistoryError;
  // "Pending" is derived from the cache rather than the slices' loading
  // flags: no data and no error for this selection means the effects above
  // have a fetch in flight for it. The flags also cover requests for other
  // selections, which must not blank data that is already on screen.
  const insightsPending = !insightsForSelection && !insightsError;
  const historyPending = !historyForSelection && !historyError;
  // The full-page spinner is for the first load only, when nothing of this
  // patient's is on screen yet. After that the view stays mounted across a
  // window change and each panel shows its own pending state.
  const showSpinner = historyPending && !historyForOwner;

  // Toggle label with the window's response count; no count while the
  // insights are missing, so a failed fetch never reads as "(0)".
  const surveyTypeLabel = (type: SurveyType) => {
    const name = SURVEY_TYPE_DISPLAY_NAME[type];
    if (!insightsForSelection) return name;
    const count =
      findSurveyBlock(insightsForSelection.surveys, type)?.response_count ?? 0;
    return `${name} (${count})`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} px={2} pt={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography
            sx={{
              color: lightBlack,
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.2px',
            }}
          >
            Survey insights
          </Typography>
          <Typography
            sx={{
              color: dimGrey,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
          >
            How this patient&apos;s survey responses are trending over time
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <ToggleButtonGroup
            value={surveyType}
            exclusive
            size="small"
            color="secondary"
            aria-label="Survey type"
            onChange={(_, value: SurveyType | null) => {
              if (value !== null) setSurveyType(value);
            }}
          >
            {INSIGHTS_SURVEY_TYPES.map((type) => (
              <ToggleButton key={type} value={type} sx={toggleSx}>
                {surveyTypeLabel(type)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={insightsWindow.days}
            exclusive
            size="small"
            color="secondary"
            aria-label="Time window"
            onChange={(_, value: number | null) => {
              const next = INSIGHTS_WINDOWS.find((w) => w.days === value);
              if (next) setInsightsWindow(next);
            }}
          >
            {INSIGHTS_WINDOWS.map((w) => (
              <ToggleButton key={w.days} value={w.days} sx={toggleSx}>
                {w.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {periodRange && (
            <Typography
              data-testid="insights-period-range"
              sx={{
                alignSelf: 'center',
                color: dimGrey,
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                whiteSpace: 'nowrap',
              }}
            >
              {periodRange}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Keyed on survey type so a new type starts the table on page 1. A
          new window keeps the view mounted; its list panel shows a spinner
          until the window's rows arrive, so the table remounts on page 1
          with them. */}
      {showSpinner ? (
        <LoadingSpinner />
      ) : (
        <SurveyInsightsView
          key={surveyType}
          surveyType={surveyType}
          block={findSurveyBlock(insightsForSelection?.surveys, surveyType)}
          period={period}
          windowLabel={insightsWindow.label}
          granularity={insightsWindow.granularity}
          rows={rows}
          insightsLoading={insightsPending}
          historyLoading={historyPending}
          insightsError={insightsError}
          historyError={historyError}
        />
      )}
    </Box>
  );
};

export default InsightsTab;
